/**
 * Saved Sections Library API
 *
 * Allows users to save section blocks (with their edited content and meta)
 * to a global library, then reuse them across pages and templates.
 */

import { apiFetch } from "./apiClient";

export interface SavedSection {
  id: string;
  name: string;
  description: string;
  blockType: string;
  content: string;
  meta?: Record<string, any>;
  variant?: string;
  savedAt: number;
  savedBy?: string;
}

/** List all saved sections (newest first). */
export async function listSavedSections(): Promise<SavedSection[]> {
  const data = await apiFetch("/saved-sections");
  return data.sections || [];
}

/** Save a new section to the library. */
export async function saveSection(
  section: Omit<SavedSection, "id" | "savedAt">,
): Promise<SavedSection> {
  const id = `ss-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const payload: SavedSection = { ...section, id, savedAt: Date.now() };
  await apiFetch("/saved-sections", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return payload;
}

/** Delete a saved section from the library. */
export async function deleteSavedSection(id: string): Promise<void> {
  await apiFetch(`/saved-sections/${id}`, { method: "DELETE" });
}

/** Update a saved section's name/description. */
export async function updateSavedSection(
  id: string,
  updates: Partial<Pick<SavedSection, "name" | "description">>,
): Promise<void> {
  await apiFetch(`/saved-sections/${id}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}
